import { Injectable } from '@angular/core'
import {
  BigCommerceCoupon,
  CouponCode,
  CouponCodeState,
  CouponCodeTargetSelection,
  CouponCodeTargetType,
  CouponCodeType,
  CouponCodeValueType,
  CouponStatus,
  GiftCardCoupon,
  GiftCardUniqueCoupon,
  ShopifyCoupon,
  UICouponCodeSummary,
  UICouponCodeTypeName,
  UICouponTargetSelection,
} from '../models/coupon-code'
import moment from 'moment'
import * as _ from 'lodash'
import { WooCommerceCoupon } from '../components/new-coupon-code/components/woo-commerce-coupon/interfaces/woo-commerce-coupon.interface'
import { DiscountType } from '../components/new-coupon-code/components/woo-commerce-coupon/enums/discount-type.enum'
import { GiftCard } from '../../gift-cards/models/gift-card'
import { TranslocoService } from '@jsverse/transloco'

@Injectable({
  providedIn: 'root'
})
export class NewCouponCodesService {
  summary: UICouponCodeSummary

  couponCodeType = CouponCodeType
  couponCodeTargetType = CouponCodeTargetType
  couponCodeTargetSelection = CouponCodeTargetSelection
  uiCouponCodeValueType = CouponCodeValueType
  uiCouponCodeTypeName = UICouponCodeTypeName
  uiCouponTargetSelection = UICouponTargetSelection
  couponCodeState = CouponCodeState
  _couponStatus = CouponStatus

  public currency: string

  constructor(private transloco: TranslocoService) {
  }

  generateRandomCode(): string {
    return (Math.random().toString(36).substring(2)).toUpperCase()
  }

  /**
   * Summary generation stuff
   */

  updateSummary(value: ShopifyCoupon | BigCommerceCoupon, randomString: string, currency: string = ''): UICouponCodeSummary {
    this.summary = {
      schedule: {
        start: this.summaryDate('Starting at', value.data.starts_at, 'LLL'),
        end: this.summaryDate('Ends at', value.data.ends_at, 'LLL'),
      },
      codeType: this._summaryCodeType(value.type),
      code: this._summaryCode(value, randomString),
      randomString: randomString,
      discount: value.type !== this.couponCodeType.manual_master ? this.summaryDiscount(value, currency) : null,
      discoutnRelatedTo: value.type !== this.couponCodeType.manual_master ? this.summaryDiscountRelatedTo(value) : null,
      limit: this.summaryUsageLimit(value),
    }
    return this.summary
  }

  public summaryUsageLimit(value: ShopifyCoupon | BigCommerceCoupon): string {
    const summaryIsforForm = !!(value.data && value.data._local)
    const t = (k: string, p?: Record<string, unknown>) => this.transloco.translate(`coupon_form.${k}`, p)
    const limitLine = (n: number, once: boolean) => {
      const base = n === 1
        ? t('summary_limit_of_1_use')
        : t('summary_limit_of_n_uses', { count: n })
      return once ? `${base}${t('summary_suffix_once_per_customer')}` : base
    }
    if (summaryIsforForm) {
      switch (value.type) {
        case this.couponCodeType.manual_master:
          return null
        case this.couponCodeType.big_commerce_master:
          return null
        case this.couponCodeType.woo_commerce_master:
          return null
        case this.couponCodeType.shopify_unique:
          return t('summary_limit_unique_coupon')
        case this.couponCodeType.big_commerce_unique:
          return t('summary_limit_unique_coupon')
        case this.couponCodeType.woo_commerce_unique:
          return t('summary_limit_unique_coupon')
        default:
          if (value.data.once_per_customer && !value.data._local.setUsageLimit) {
            return t('summary_one_use_per_customer')
          } else if (value.data._local.setUsageLimit && value.data.usage_limit !== 0) {
            return limitLine(value.data.usage_limit, value.data.once_per_customer)
          } else if (value.data._local.setUsageLimit && value.data.usage_limit === 0) {
            return t('summary_one_use_per_customer')
          } else {
            return null
          }
      }
    } else {
      switch (value.type) {
        case this.couponCodeType.manual_master:
          return null
        case this.couponCodeType.big_commerce_master:
          return null
        case this.couponCodeType.shopify_unique:
          return t('summary_limit_unique_coupon')
        case this.couponCodeType.big_commerce_unique:
          return t('summary_limit_unique_coupon')
        default:
          if (value.data.once_per_customer) {
            return t('summary_one_use_per_customer')
          } else if (!value.data.once_per_customer && value.data.usage_limit !== 0) {
            return limitLine(value.data.usage_limit, value.data.once_per_customer)
          } else if (value.data.usage_limit === 0) {
            return t('summary_one_use_per_customer')
          } else {
            return null
          }
      }
    }
  }

  public summaryDiscountRelatedTo(value: ShopifyCoupon | BigCommerceCoupon): string {
    const t = (k: string) => this.transloco.translate(`coupon_form.${k}`)
    if (value.data.target_type === CouponCodeTargetType.shipping_line) {
      return t('summary_on_entire_order')
    }
    switch (value.data.target_selection) {
      case this.couponCodeTargetSelection.all:
        return t('summary_for_all_products')
      case this.couponCodeTargetSelection.entitled:
        return t('summary_for_specific_products')
      default:
        return null
    }
  }

  public couponStatus(coupon: ShopifyCoupon | BigCommerceCoupon | GiftCardCoupon | GiftCardUniqueCoupon): string {
    if (coupon.status === this._couponStatus.archived) {
      return this.couponCodeState.archived
    }

    if (coupon.type === this.couponCodeType.manual_master || coupon.type === this.couponCodeType.manual_unique) {
      return this.couponCodeState.active
    }

    if (coupon.status === this._couponStatus.disabled) {
      return this.couponCodeState.disabled
    }

    const today = moment()
    const startDate = _.get(coupon, 'data.starts_at')
    const endDate = _.get(coupon, 'data.ends_at')

    if (endDate && moment(endDate).isBefore(today)) {
      return this.couponCodeState.expired
    }

    if (startDate && moment(startDate).isAfter(today)) {
      return this.couponCodeState.scheduled
    }

    return this.couponCodeState.active
  }

  public getCouponTypeName(type: CouponCodeType) {
    return this.uiCouponCodeTypeName[type]
  }


  public getDiscountText(reward: CouponCode | GiftCard, currency?: string): string {
    let discount
    let discountRelatedTo
    switch (reward.type) {
      case this.couponCodeType.shopify_master:
      case this.couponCodeType.shopify_unique:
        discount = this.summaryDiscount(reward as ShopifyCoupon, currency)
        discountRelatedTo = this.summaryDiscountRelatedTo(reward as ShopifyCoupon)
        return `${discount} ${discountRelatedTo}`
      case this.couponCodeType.big_commerce_master:
      case this.couponCodeType.big_commerce_unique:
        discount = this.summaryDiscount(reward as BigCommerceCoupon, currency)
        discountRelatedTo = this.summaryDiscountRelatedTo(reward as BigCommerceCoupon)
        return `${discount} ${discountRelatedTo}`
      case this.couponCodeType.shopify_gift_card:
      case this.couponCodeType.shopify_gift_card_unique:
        return this.summaryCash(reward as GiftCardUniqueCoupon | GiftCardUniqueCoupon, currency)
      default:
        return null
    }
  }

  public getDiscountTextShort(reward: CouponCode | GiftCard, currency?: string): string {
    let discount
    switch (reward.type) {
      case this.couponCodeType.shopify_master:
      case this.couponCodeType.shopify_unique:
        discount = this.summaryDiscount(reward as ShopifyCoupon, currency)
        return `${discount}`
      default:
        return null
    }
  }

  public summaryCash(value: GiftCardCoupon | GiftCardUniqueCoupon, currency?: string): string {
    return this.transloco.translate('coupon_form.summary_gift_value', {
      currency: currency || '',
      amount: value.value,
    })
  }

  public summaryDiscount(value: ShopifyCoupon | BigCommerceCoupon | WooCommerceCoupon, currency?: string): string {
    const t = (k: string, p?: Record<string, unknown>) => this.transloco.translate(`coupon_form.${k}`, p)
    if (value.type === CouponCodeType.manual_master) {
      return t('summary_manual')
    }

    if ((value as ShopifyCoupon | BigCommerceCoupon).data.target_type) {
      switch ((value as ShopifyCoupon | BigCommerceCoupon).data.target_type) {
        case this.couponCodeTargetType.line_item:
          if (value.data.value_type === this.uiCouponCodeValueType.percentage) {
            return t('summary_pct_off', { pct: value.data.value })
          } else if (value.data.value_type === this.uiCouponCodeValueType.buy_x_get_y) {
            const buyCount = value.data.prerequisite_quantity_range_min
            const getCount = value.data.customer_get_quantity
            const getSpend = value.data.prerequisite_subtotal_min
            const discount = value.data.customer_get_percentage
            const discountStr = discount === 1
              ? t('summary_bxgy_for_free')
              : t('summary_bxgy_at_pct_off', { pct: (discount * 100).toFixed(0) })
            if (getSpend) {
              const spend = currency ? `${currency} ${getSpend}` : `${getSpend}`
              return t('summary_bxgy_spend_line', {
                spend,
                getCount,
                discountPart: discountStr,
              })
            } else {
              return t('summary_bxgy_buy_line', {
                buyCount,
                getCount,
                discountPart: discountStr,
              })
            }
          } else if (value.data.value_type === this.uiCouponCodeValueType.fixed_amount) {
            return t('summary_fixed_off', {
              currency: currency ? `${currency} ` : '',
              amount: value.data.value,
            })
          } else {
            return null
          }
        case this.couponCodeTargetType.shipping_line:
          return t('summary_free_shipping')
        default:
          return null
      }
    } else {
      const freeShipping = (value as WooCommerceCoupon).data.free_shipping
      if (value.data.value !== 0) {
        switch ((value as WooCommerceCoupon).data.value_type) {
          case DiscountType.Percentage:
            return freeShipping
              ? t('summary_woo_pct_off_plus_fs', { pct: value.data.value })
              : t('summary_pct_off', { pct: value.data.value })
          case DiscountType.FixedProduct:
          case DiscountType.FixedCart:
            return freeShipping
              ? t('summary_woo_fixed_off_plus_fs', {
                  currency: currency ? `${currency} ` : '',
                  amount: value.data.value,
                })
              : t('summary_fixed_off', {
                  currency: currency ? `${currency} ` : '',
                  amount: value.data.value,
                })
        }
      } else {
        if (freeShipping) {
          return t('summary_free_shipping')
        }
      }

    }
    return null
  }

  static randomString(length) {
    const chars = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ'
    let result = ''
    for (let i = length; i > 0; --i) result += chars[Math.floor(Math.random() * chars.length)]
    return result.toUpperCase()
  }

  private _summaryCode(value: ShopifyCoupon | BigCommerceCoupon, randomString: string): string {
    switch (value.type) {
      case this.couponCodeType.shopify_master:
        return value.master_code
      case this.couponCodeType.big_commerce_master:
        return value.master_code
      case this.couponCodeType.woo_commerce_master:
        return value.master_code
      case this.couponCodeType.manual_master:
        return value.master_code
      case this.couponCodeType.shopify_unique:
        return `${value.prefix_code}${randomString}`
      case this.couponCodeType.shopify_gift_card_unique:
        return `${value.prefix_code}${randomString}`
      case this.couponCodeType.big_commerce_unique:
        return `${value.prefix_code}${randomString}`
      case this.couponCodeType.woo_commerce_unique:
        return `${value.prefix_code}${randomString}`
      default:
        return null
    }
  }

  private _summaryCodeType(codeType: CouponCodeType): string {
    switch (codeType) {
      case this.couponCodeType.shopify_master:
        return 'Same code for all Pop Ups'
      case this.couponCodeType.big_commerce_master:
        return 'Same code for all Pop Ups'
      case this.couponCodeType.woo_commerce_master:
        return 'Same code for all Pop Ups'
      case this.couponCodeType.manual_master:
        return 'Same code for all Pop Ups'
      case this.couponCodeType.shopify_unique:
        return 'Custom code for each Pop Up'
      case this.couponCodeType.shopify_gift_card_unique:
        return 'Custom code for each Pop Up'
      case this.couponCodeType.big_commerce_unique:
        return 'Custom code for each Pop Up'
      case this.couponCodeType.woo_commerce_unique:
        return 'Custom code for each Pop Up'
      default:
        return null
    }
  }

  public summaryDate(prepend: string, date: string, format: string): string {
    if (!date || !date.length) {
      return null
    }
    return `${prepend} ${moment(date).format(format)}`
  }
}
